import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Shield,
  Clock,
  CheckCircle,
  MapPin,
  Mail,
  Phone,
  Menu,
  X,
  ArrowRight,
  FileCheck,
  Wallet,
  Zap,
  Building2,
  Lock,
  Users,
  MessageCircle,
  TrendingUp,
  Award,
  ThumbsUp,
  BadgeCheck,
  FileText,
  Star
} from 'lucide-react';

const AnimatedStat = ({ stat, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="relative text-center py-8 px-4 rounded-2xl bg-white shadow-md hover:shadow-xl transition-all cursor-default overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: stat.color }} />
      <p className="text-4xl md:text-5xl font-extrabold mb-2" style={{ color: stat.color }}>
        {inView ? (
          <CountUp end={stat.end} duration={2} separator={stat.separator || ''} suffix={stat.suffix} />
        ) : '0'}
      </p>
      <p className="text-sm md:text-base text-gray-500 font-medium">{stat.label}</p>
    </motion.div>
  );
};

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', message: '' });
  const [showPlane, setShowPlane] = useState(true);
  
  // Simulador de cuotas
  const [loanAmount, setLoanAmount] = useState(150000);
  const [loanAmountInput, setLoanAmountInput] = useState('150.000');
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [installments, setInstallments] = useState(6);
  const rafRef = useRef(null);
  
  const handleSliderChange = useCallback((e) => {
    const value = Number(e.target.value);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setLoanAmount(value);
    });
  }, []);
  
  // Tasa de interés mensual: 13.5% TEM (Tasa Efectiva Mensual)
  const monthlyRate = 0.135;
  
  // Calcular cuota mensual usando fórmula de amortización francesa
  const calculateMonthlyPayment = (amount, months) => {
    if (months === 0) return 0;
    const rate = monthlyRate;
    const payment = amount * (rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    return Math.round(payment);
  };

  // Función para formatear el número
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Función para limpiar el número
  const cleanNumber = (str) => {
    return parseInt(str.replace(/\./g, '').replace(/\$/g, '').replace(/\D/g, '')) || 0;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 5);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const handleCTAClick = () => {
    window.open('https://online.creditomovil.com.ar', '_blank');
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5492945331566', '_blank');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const message = `Hola, soy ${formData.name}. ${formData.message}`;
    window.open(`https://wa.me/5492945331566?text=${encodeURIComponent(message)}`, '_blank');
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#1d8fce] shadow-lg ${
          isScrolled ? 'py-2' : 'py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo - Left side, smaller */}
            <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('inicio')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_credito-movil/artifacts/51xelsge_29BCF44D-BFF8-4668-BE7A-9F88155464F3.PNG" 
                alt="Crédito Móvil" 
                className={`transition-all duration-300 ${isScrolled ? 'h-6 md:h-8' : 'h-9 md:h-10'}`}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2 lg:space-x-4 ml-0">
              <button onClick={() => scrollToSection('como-funciona')} className="text-white hover:text-[#FDB913] font-medium transition-colors text-xs lg:text-sm whitespace-nowrap">
                Cómo funciona
              </button>
              <button onClick={() => scrollToSection('beneficios')} className="text-white hover:text-[#FDB913] font-medium transition-colors text-xs lg:text-sm whitespace-nowrap">
                ¿Por qué elegirnos?
              </button>
              <button onClick={() => scrollToSection('nosotros')} className="text-white hover:text-[#FDB913] font-medium transition-colors text-xs lg:text-sm whitespace-nowrap">
                Nosotros
              </button>
              <button onClick={() => scrollToSection('preguntas')} className="text-white hover:text-[#FDB913] font-medium transition-colors text-xs lg:text-sm whitespace-nowrap">
                Preguntas frecuentes
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-white hover:text-[#FDB913] font-medium transition-colors text-xs lg:text-sm whitespace-nowrap">
                Contacto
              </button>
              <Button 
                onClick={() => window.open('https://online.creditomovil.com.ar', '_blank')}
                className="text-gray-900 font-bold px-3 lg:px-5 py-2 shadow-lg hover:shadow-xl transition-all border border-[#C5D9E8] text-xs lg:text-sm whitespace-nowrap"
                style={{ 
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #D6E9F5 0%, #E3F0F8 35%, #F0F7FB 70%, #FFFFFF 100%)'
                }}
              >
                Paga tu cuota
              </Button>
              <Button 
                onClick={handleCTAClick}
                className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold px-3 lg:px-5 py-2 shadow-lg hover:shadow-xl transition-all text-xs lg:text-sm whitespace-nowrap"
                style={{ borderRadius: '12px' }}
              >
                Solicitar Crédito
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#1678b3] border-t border-white/20 py-4"
          >
            <div className="container mx-auto px-4 flex flex-col space-y-4">
              <button onClick={() => scrollToSection('como-funciona')} className="text-white hover:text-[#FDB913] font-medium text-left">
                Cómo funciona
              </button>
              <button onClick={() => scrollToSection('beneficios')} className="text-white hover:text-[#FDB913] font-medium text-left">
                ¿Por qué elegirnos?
              </button>
              <button onClick={() => scrollToSection('nosotros')} className="text-white hover:text-[#FDB913] font-medium text-left">
                Nosotros
              </button>
              <button onClick={() => scrollToSection('preguntas')} className="text-white hover:text-[#FDB913] font-medium text-left">
                Preguntas frecuentes
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-white hover:text-[#FDB913] font-medium text-left">
                Contacto
              </button>
              <Button 
                onClick={() => window.open('https://online.creditomovil.com.ar', '_blank')}
                className="text-gray-900 font-bold w-full shadow-lg border border-[#C5D9E8]"
                style={{ 
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #D6E9F5 0%, #E3F0F8 35%, #F0F7FB 70%, #FFFFFF 100%)'
                }}
              >
                Paga tu cuota
              </Button>
              <Button 
                onClick={handleCTAClick}
                className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold w-full shadow-lg"
                style={{ borderRadius: '12px' }}
              >
                Solicitar Crédito
              </Button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Hero Section - Split Layout */}
      <section id="inicio" className="pt-24 md:pt-28 pb-6 md:pb-8" style={{ background: 'linear-gradient(to bottom right, #d6e2fc, #ffffff)' }}>
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="w-full max-w-6xl mx-auto"
          >
            {/* Desktop: 2 columnas | Mobile: stack centrado */}
            <div className="flex flex-col md:flex-row md:items-center md:gap-12">
              
              {/* Columna izquierda: Textos + Botones */}
              <div className="text-center md:text-center md:flex-1">
                <motion.h1 variants={fadeInUp} className="text-3xl md:text-5xl lg:text-7xl font-bold text-[#1d8fce] mb-1">
                  100% ONLINE
                </motion.h1>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-2">
                  Acreditación Inmediata
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-xl md:text-2xl lg:text-3xl text-gray-600 font-medium mb-8">
                  La cuota más baja del mercado!
                </motion.p>

                {/* CTA Solicitar Crédito */}
                <motion.div variants={fadeInUp}>
                  <Button 
                    onClick={handleCTAClick}
                    size="lg"
                    className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all group"
                    style={{ borderRadius: '16px' }}
                  >
                    Solicitar Crédito
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>

                {/* Spacer para alinear verticalmente */}
              </div>

              {/* Columna derecha: Simulador */}
              <motion.div variants={fadeInUp} className="w-full max-w-md md:max-w-lg mx-auto md:mx-0 md:flex-1 mt-8 md:mt-0">
                <div className="bg-[#1d8fce] p-5 md:p-6 rounded-2xl text-white shadow-lg text-center">
                  <p className="text-3xl md:text-4xl font-extrabold text-[#FDB913] mb-3">¡HASTA $6.000.000!</p>
                    
                    {/* Monto seleccionado */}
                    <div className="mb-4">
                      <div className="flex flex-col items-center mb-2">
                        <span className="text-xs font-medium mb-2">Monto:</span>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-2xl font-bold pointer-events-none">$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={isEditingAmount ? loanAmountInput : formatNumber(loanAmount)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^\d.]/g, '');
                              setLoanAmountInput(raw);
                              
                              const numValue = parseInt(raw.replace(/\./g, '')) || 0;
                              if (numValue >= 50000 && numValue <= 6000000) {
                                setLoanAmount(numValue);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            onBlur={() => {
                              let numValue = parseInt(loanAmountInput.replace(/\./g, '')) || 0;
                              
                              if (numValue < 50000) {
                                numValue = 50000;
                              } else if (numValue > 6000000) {
                                numValue = 6000000;
                              } else {
                                numValue = Math.round(numValue / 10000) * 10000;
                              }
                              
                              setLoanAmount(numValue);
                              setLoanAmountInput(formatNumber(numValue));
                              setIsEditingAmount(false);
                            }}
                            onFocus={(e) => {
                              setIsEditingAmount(true);
                              setLoanAmountInput(formatNumber(loanAmount));
                              setTimeout(() => e.target.select(), 10);
                            }}
                            className="w-52 pl-8 pr-3 py-1 text-2xl font-bold text-right bg-white/10 border-2 border-white/30 rounded-lg focus:border-[#FDB913] focus:outline-none transition-all"
                            placeholder="500.000"
                          />
                        </div>
                      </div>
                      
                      {/* Slider para monto */}
                      <input
                        type="range"
                        min="50000"
                        max="6000000"
                        step="10000"
                        value={loanAmount}
                        onChange={handleSliderChange}
                        className="w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider touch-none"
                        style={{
                          background: `linear-gradient(to right, #FDB913 0%, #FDB913 ${((loanAmount - 50000) / (6000000 - 50000)) * 100}%, rgba(255,255,255,0.3) ${((loanAmount - 50000) / (6000000 - 50000)) * 100}%, rgba(255,255,255,0.3) 100%)`
                        }}
                      />
                      <div className="flex justify-between text-xs mt-1 opacity-90">
                        <span>$50.000</span>
                        <span>$6.000.000</span>
                      </div>
                    </div>
                    
                    {/* Iniciar Solicitud dentro del simulador */}
                    <Button 
                      onClick={handleCTAClick}
                      size="lg"
                      className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold py-4 text-lg shadow-xl hover:shadow-2xl transition-all group w-full mt-3"
                      style={{ borderRadius: '12px' }}
                    >
                      Iniciar Solicitud
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <p className="text-xs opacity-70 mt-2">*Sujeto a verificación crediticia</p>
                  </div>

                  {/* Botones debajo del simulador - mismo ancho */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <Button 
                      onClick={() => scrollToSection('como-funciona')}
                      size="lg"
                      className="bg-[#1d8fce] hover:bg-[#1678b3] text-white font-bold py-5 text-base transition-all flex-1"
                      style={{ borderRadius: '12px' }}
                    >
                      Ver cómo funciona
                    </Button>
                    <Button 
                      onClick={() => window.open('https://online.creditomovil.com.ar', '_blank')}
                      size="lg"
                      className="text-gray-900 font-bold py-5 text-base shadow-lg transition-all border border-[#A8CFEB] flex-1"
                      style={{ 
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #A8CFEB 0%, #C0DDEF 35%, #D8EBF7 70%, #FFFFFF 100%)'
                      }}
                    >
                      Paga tu cuota
                    </Button>
                  </div>
                </motion.div>
            </div>

            {/* Trust Chips */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 pt-16 md:pt-20 justify-center">
              <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-md border border-gray-200">
                <Award className="w-5 h-5 text-[#1d8fce] mr-2" />
                <span className="text-sm font-semibold text-gray-700">+20 años de experiencia</span>
              </div>
              <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-md border border-gray-200">
                <Building2 className="w-5 h-5 text-[#1d8fce] mr-2" />
                <span className="text-sm font-semibold text-gray-700">Sucursales físicas</span>
              </div>
              <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-md border border-gray-200">
                <Zap className="w-5 h-5 text-[#FDB913] mr-2" />
                <span className="text-sm font-semibold text-gray-700">Aprobación inmediata</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ¿Cómo saco un préstamo? */}
      <section id="como-funciona" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
              ¿Cómo saco un préstamo?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {[
              { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/5e4fbb179e7aeac60767e94beead629189285d204d770b5661c5f812a27bf85b.png', label: 'Ingresá tus datos' },
              { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/4fd17b50149fcfb1ca32c55921149554e0d7b71fed0a4f02fec6dbdcfe288d1a.png', label: 'Validá tu identidad' },
              { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/3fd0fc99bc7591cea66ef47be9fa890e601b902778f1ad5069fc80a8ed6d2ec9.png', label: 'Recibí tu préstamo' }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="flex flex-col items-center text-center"
              >
                <div className="w-48 h-48 md:w-56 md:h-56 flex items-center justify-center mb-6">
                  <img src={step.img} alt={step.label} className="w-40 h-40 md:w-48 md:h-48 object-contain" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1d8fce]">{step.label}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Qué necesito */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8"
              >
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 italic">¿Qué necesito?</h2>
              </motion.div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {[
                  { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/35bd9b032687d8a398407a182b7a1309671d1d49740a0cd84735b7ba730a2472.png', label: 'Ser mayor de 21 años y tener DNI Argentino' },
                  { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/11373541cc5264ebeb6d799e12cb1fa80873de8c1ee98ecd5c89c5b6774b0944.png', label: 'Tener un número de celular y un mail' },
                  { img: 'https://static.prod-images.emergentagent.com/jobs/362739e3-6f8a-4ae2-9b1c-0df90eb21381/images/e1cdec5595a86c9f7fe57f76bc8ea4a112c1a1050f1fc0cf14908c3ed14a5e8e.png', label: 'Debés tener ingresos en blanco' }
                ].map((req, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    className="flex flex-col items-center text-center"
                  >
                    <img src={req.img} alt={req.label} className="w-20 h-20 md:w-24 md:h-24 object-contain mb-4" />
                    <p className="text-base md:text-xl font-bold text-[#1d8fce]">{req.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* CTA Solicitalo ahora */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center mt-10"
            >
              <Button 
                onClick={handleCTAClick}
                size="lg"
                className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all group"
                style={{ borderRadius: '16px' }}
              >
                Solicitalo ahora
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Beneficios Section - Enhanced */}
      <section id="beneficios" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              ¿Por qué elegirnos?
            </h2>
            <p className="text-xl text-gray-600">La combinación perfecta de experiencia y tecnología</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-10 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 group" style={{ borderRadius: '24px' }}>
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full border-2 border-[#1d8fce] flex items-center justify-center group-hover:bg-[#1d8fce]/10 transition-colors">
                    <Shield className="w-10 h-10 text-[#1d8fce]" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Seguridad</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Más de 20 años de experiencia nos respaldan. Somos una institución con presencia física y compromiso real con vos.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="p-10 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 group" style={{ borderRadius: '24px' }}>
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full border-2 border-[#FDB913] flex items-center justify-center group-hover:bg-[#FDB913]/10 transition-colors">
                    <Clock className="w-10 h-10 text-[#FDB913]" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Rapidez</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Aprobación inmediata. El dinero en tu cuenta cuando más lo necesitás. Sin esperas innecesarias.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="p-10 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 group" style={{ borderRadius: '24px' }}>
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full border-2 border-[#1d8fce] flex items-center justify-center group-hover:bg-[#1d8fce]/10 transition-colors">
                    <CheckCircle className="w-10 h-10 text-[#1d8fce]" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Confianza</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Soluciones claras y responsables. Sin letra chica. Hablamos tu idioma y entendemos tus necesidades.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nosotros - Sección unificada */}
      <section id="nosotros" className="py-20 md:py-28 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
                Nosotros
              </h2>
            </motion.div>

            {/* Stats con contador animado */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {[
                { end: 20, suffix: '+', label: 'Años de experiencia', color: '#1d8fce' },
                { end: 5000, separator: '.', suffix: '+', label: 'Clientes satisfechos', color: '#FDB913' },
                { end: 100, suffix: '%', label: 'Proceso online', color: '#1d8fce' },
                { end: 2, suffix: '', label: 'Sucursales físicas', color: '#FDB913' }
              ].map((stat, index) => (
                <AnimatedStat key={index} stat={stat} index={index} />
              ))}
            </div>

            {/* Bullets */}
            <div className="flex flex-col items-center gap-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-4 w-full max-w-3xl"
              >
                {[
                  { bold: 'Más de 20 años otorgando préstamos personales', text: ' con soluciones claras y responsables en la Patagonia.' },
                  { bold: 'Pioneros en la región con sistema 100% online', text: ' que une trayectoria real con agilidad digital.' },
                  { bold: 'Sucursales físicas', text: ' en Esquel y Trevelin donde podés conocernos personalmente.' },
                  { bold: 'Confianza y rapidez', text: '. No tenés que elegir entre las dos. Con nosotros, tenés ambas.' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className="flex items-start space-x-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#1d8fce]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#1d8fce]" />
                    </div>
                    <p className="text-gray-600 text-sm md:text-base">
                      <strong className="text-gray-900">{item.bold}</strong>{item.text}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="preguntas" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Preguntas frecuentes
            </h2>
            <p className="text-xl text-gray-600">Todo lo que necesitás saber</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Cuánto tarda la aprobación?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  La evaluación de tu solicitud es inmediata. Tenés una respuesta al instante.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Qué documentación necesito?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Solo necesitás tu DNI. Todo se gestiona de forma digital y rápida.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Cuándo recibo el dinero?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Una vez aprobado tu crédito, el dinero se acredita en tu cuenta de manera inmediata.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Cómo pago mis cuotas?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  En{' '}
                  <a 
                    href="https://online.creditomovil.com.ar" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#1d8fce] hover:text-[#1678b3] font-semibold underline transition-colors"
                  >
                    online.creditomovil.com.ar
                  </a>
                  {' '}te figurarán todos tus préstamos y podés pagarlos con MercadoPago, tarjeta de débito, efectivo en sucursal, Rapipago o Pago Fácil.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Puedo pagar anticipadamente?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Sí, podés cancelar tu préstamo en cualquier momento sin penalidades. Hablamos claro desde el principio.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white rounded-2xl px-6 border-2 border-gray-100">
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-[#1d8fce]">
                  ¿Tienen atención presencial?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Sí, contamos con dos sucursales físicas en Esquel y Trevelin donde podés visitarnos personalmente.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section with Form */}
      <section id="contacto" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Contacto
            </h2>
            <p className="text-xl text-gray-600">Estamos para ayudarte</p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-6">
            {/* Contact Info - Más compacto */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4 hover:shadow-lg transition-all bg-white border-2 border-gray-100" style={{ borderRadius: '16px' }}>
                <div className="flex items-center space-x-3">
                  <div className="bg-[#25D366] p-2 rounded-full">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">WhatsApp</h3>
                    <button 
                      onClick={handleWhatsAppClick}
                      className="text-[#1d8fce] hover:text-[#1678b3] font-semibold transition-colors"
                    >
                      +54 9 2945 331566
                    </button>
                  </div>
                </div>
              </Card>

              <Card className="p-4 hover:shadow-lg transition-all bg-white border-2 border-gray-100" style={{ borderRadius: '16px' }}>
                <div className="flex items-center space-x-3">
                  <div className="bg-[#1d8fce] p-2 rounded-full">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Email</h3>
                    <a 
                      href="mailto:info@creditomovil.com.ar"
                      className="text-[#1d8fce] hover:text-[#1678b3] font-semibold transition-colors text-sm"
                    >
                      info@creditomovil.com.ar
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form - Minimalista y ANTES de los mapas */}
            <Card className="p-5 bg-white border-2 border-gray-100 max-w-3xl mx-auto" style={{ borderRadius: '16px' }}>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Envianos un mensaje</h3>
              <form onSubmit={handleFormSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Nombre</label>
                  <input 
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-[#1d8fce] focus:outline-none transition-colors"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Mensaje</label>
                  <textarea 
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-[#1d8fce] focus:outline-none transition-colors resize-none"
                    placeholder="¿En qué podemos ayudarte?"
                  ></textarea>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold py-2.5 shadow-lg hover:shadow-xl transition-all"
                  style={{ borderRadius: '10px' }}
                >
                  Enviar mensaje
                </Button>
              </form>
            </Card>

            {/* Sucursales - Mapas minimalistas */}
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-gray-900 text-center">Nuestras sucursales</h3>
              
              {/* Mapas de Google Maps - Más pequeños */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Mapa Esquel */}
                <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                  <div className="bg-[#1d8fce] text-white px-2 py-1 text-xs font-semibold">
                    📍 Esquel - 25 de Mayo 528
                  </div>
                  <iframe
                    src="https://maps.google.com/maps?q=Crédito+Móvil+Esquel+25+de+Mayo+528+Chubut+Argentina&t=&z=16&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mapa Esquel"
                  ></iframe>
                </div>

                {/* Mapa Trevelin */}
                <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                  <div className="bg-[#1d8fce] text-white px-2 py-1 text-xs font-semibold">
                    📍 Trevelin - San Martín 419
                  </div>
                  <iframe
                    src="https://maps.google.com/maps?q=San+Martín+419,+Trevelin,+Chubut,+Argentina&t=&z=16&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mapa Trevelin"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="bg-[#1d8fce] rounded-2xl p-10 md:p-14 text-white max-w-3xl mx-auto">
              <h3 className="text-3xl md:text-5xl font-bold mb-2">
                Solicitalo ahora
              </h3>
              <p className="text-lg md:text-2xl mb-10 text-white/80 font-light">
                y recibí tu transferencia al instante
              </p>
              <Button 
                onClick={handleCTAClick}
                size="lg"
                className="bg-[#FDB913] hover:bg-[#FCA311] text-black font-bold px-10 py-6 text-lg md:text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all group"
                style={{ borderRadius: '14px' }}
              >
                Solicitar mi crédito
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legales Section */}
      <section className="py-16 md:py-20 bg-gray-50 border-t-4 border-[#1d8fce]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Información Legal
            </h2>
            <p className="text-xl text-gray-600">Transparencia y seguridad en cada operación</p>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {/* Información al Usuario Financiero */}
              <AccordionItem value="legal-1" className="bg-white rounded-2xl px-6 border-2 border-gray-200">
                <AccordionTrigger className="text-left text-xl font-bold hover:text-[#1d8fce]">
                  Información al Usuario Financiero
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 space-y-4 pt-4">
                  <div className="bg-[#1d8fce] text-white p-6 rounded-xl mb-4">
                    <h3 className="text-2xl font-bold mb-4">Servicio de Atención a usuarios de servicios financieros</h3>
                    <p className="text-sm mb-2">Resolución N° 40.126</p>
                  </div>

                  <div className="space-y-4">
                    <p className="leading-relaxed">
                      Los Responsables del Servicio de Atención al Usuario de Servicios Financieros designados ante el Banco Central de la República de Argentina son:
                    </p>

                    <div className="grid md:grid-cols-3 gap-6 my-6">
                      <div className="bg-gradient-to-br from-[#1d8fce]/10 to-white p-6 rounded-xl border-2 border-[#1d8fce]/20">
                        <h4 className="font-bold text-lg text-[#1d8fce] mb-2">Responsable Titular</h4>
                        <p className="font-semibold text-gray-900">Ismael Oscar Bullone</p>
                        <p className="text-gray-600 text-sm mt-2">E-mail: <a href="mailto:ibullone@gmail.com" className="text-[#1d8fce] hover:underline">ibullone@gmail.com</a></p>
                      </div>

                      <div className="bg-gradient-to-br from-[#1d8fce]/10 to-white p-6 rounded-xl border-2 border-[#1d8fce]/20">
                        <h4 className="font-bold text-lg text-[#1d8fce] mb-2">Responsable Suplente</h4>
                        <p className="font-semibold text-gray-900">Miguel Angel Serna</p>
                        <p className="text-gray-600 text-sm mt-2">E-mail: <a href="mailto:info@creditomovil.com.ar" className="text-[#1d8fce] hover:underline">info@creditomovil.com.ar</a></p>
                      </div>

                      <div className="bg-gradient-to-br from-[#FDB913]/10 to-white p-6 rounded-xl border-2 border-[#FDB913]/20">
                        <h4 className="font-bold text-lg text-gray-900 mb-2">Dirección</h4>
                        <p className="text-gray-700 font-medium">25 de Mayo 528 – Esquel, Chubut</p>
                        <p className="text-gray-600 text-sm mt-2">Teléfono: 02946 – 454638</p>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-[#1d8fce]">
                      <p className="font-semibold text-gray-900 mb-3">En cumplimiento de la Comunicación "A" 5460 del Banco Central de Argentina, ante cualquier consulta o reclamo, la entidad solicitará la siguiente información obligatoria:</p>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        <li>Nombre y apellidos, tipo y número de documento</li>
                        <li>E-mail, país y número de boleto en caso que el reclamo o la consulta obedezca a una operación cambiaria ya realizada</li>
                      </ul>
                    </div>

                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                      <p className="text-gray-700 leading-relaxed mb-4">
                        La Entidad analizará y responderá su consulta o reclamo en un plazo máximo de 20 días hábiles desde su presentación, proporcionándole al momento de la recepción o en un plazo máximo de 3 días, un número que identifica su consulta o reclamo que servirá de referencia para dar seguimiento o la resolución del trámite.
                      </p>
                      <p className="text-gray-700 leading-relaxed font-semibold">
                        La falta de respuesta o de conformidad con la resolución adoptada por Crédito Móvil S.A. podrá ser denunciada al Banco Central de la República de Argentina, a través de su "Centro de Atención al Usuario de Servicios Financieros" en el teléfono gratuito 0-800-999-6683, de 9 a 18 horas.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-center">
                      <a href="https://www.usuariosfinancieros.gob.ar" target="_blank" rel="noopener noreferrer">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_easycredit-2/artifacts/7wsq3t5y_brca_naranja-1.png"
                          alt="Banco Central de la República Argentina"
                          className="h-20 hover:scale-105 transition-transform"
                        />
                      </a>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Defensa de las y los Consumidores */}
              <AccordionItem value="legal-2" className="bg-white rounded-2xl px-6 border-2 border-gray-200">
                <AccordionTrigger className="text-left text-xl font-bold hover:text-[#1d8fce]">
                  Defensa de las y los Consumidores
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 space-y-4 pt-4">
                  <div className="space-y-4">
                    <p className="text-lg leading-relaxed">
                      En cumplimiento de la <strong>Ley N° 24.240 de Defensa del Consumidor</strong> y la <strong>Resolución N° 424/2020</strong> de la Secretaría de Comercio Interior, Crédito Móvil S.A. pone a disposición la siguiente información:
                    </p>

                    <div className="bg-gradient-to-br from-[#1d8fce]/10 to-white p-6 rounded-xl border-2 border-[#1d8fce]/20">
                      <h4 className="font-bold text-xl text-gray-900 mb-4">Tus derechos como consumidor:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-900">Información clara y veraz</p>
                            <p className="text-gray-600 text-sm">Derecho a recibir información completa sobre productos y servicios</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-900">Trato digno y equitativo</p>
                            <p className="text-gray-600 text-sm">Sin discriminación en la contratación</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-900">Protección contra cláusulas abusivas</p>
                            <p className="text-gray-600 text-sm">Condiciones justas en todos los contratos</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-900">Reclamos y devoluciones</p>
                            <p className="text-gray-600 text-sm">Canales claros para consultas y reclamos</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-6 rounded-xl border-l-4 border-[#FDB913]">
                      <h4 className="font-bold text-lg text-gray-900 mb-3">¿Tenés algún reclamo?</h4>
                      <p className="text-gray-700 mb-3">Podés comunicarte a través de:</p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center space-x-2">
                          <Phone className="w-5 h-5 text-[#1d8fce]" />
                          <span><strong>WhatsApp:</strong> +54 9 2945 331566</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Mail className="w-5 h-5 text-[#1d8fce]" />
                          <span><strong>Email:</strong> info@creditomovil.com.ar</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <MapPin className="w-5 h-5 text-[#1d8fce]" />
                          <span><strong>Presencial:</strong> 25 de Mayo 528, Esquel o San Martín 419, Trevelin</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                      <p className="text-gray-700 leading-relaxed">
                        También podés comunicarte con la <strong>Dirección Nacional de Defensa del Consumidor</strong> al teléfono gratuito <strong className="text-[#1d8fce]">0800-666-1518</strong> o visitar <a href="https://www.argentina.gob.ar/defensa-consumidor" target="_blank" rel="noopener noreferrer" className="text-[#1d8fce] hover:underline font-semibold">argentina.gob.ar/defensa-consumidor</a>
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Protección de Datos Personales */}
              <AccordionItem value="legal-3" className="bg-white rounded-2xl px-6 border-2 border-gray-200">
                <AccordionTrigger className="text-left text-xl font-bold hover:text-[#1d8fce]">
                  Protección de Datos Personales
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 space-y-4 pt-4">
                  <div className="space-y-4">
                    <p className="text-lg leading-relaxed">
                      En cumplimiento de la <strong>Ley N° 25.326 de Protección de Datos Personales</strong>, Crédito Móvil S.A. garantiza la privacidad y protección de tus datos.
                    </p>

                    <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-200">
                      <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                        <Lock className="w-6 h-6 text-[#1d8fce] mr-2" />
                        Compromiso de Seguridad
                      </h4>
                      <ul className="space-y-3">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                          <span><strong>Confidencialidad:</strong> Tus datos personales son tratados con absoluta confidencialidad y no serán compartidos con terceros sin tu consentimiento.</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                          <span><strong>Seguridad:</strong> Utilizamos protocolos de seguridad para proteger tu información contra accesos no autorizados.</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                          <span><strong>Finalidad:</strong> Solo utilizamos tus datos para la gestión de tu préstamo y comunicaciones relacionadas.</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-[#1d8fce]">
                      <h4 className="font-bold text-lg text-gray-900 mb-3">Tus Derechos sobre tus Datos:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[#1d8fce] rounded-full"></div>
                          <span className="font-semibold">Acceso:</span>
                          <span className="text-gray-600">Consultar qué datos tenemos</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[#1d8fce] rounded-full"></div>
                          <span className="font-semibold">Rectificación:</span>
                          <span className="text-gray-600">Corregir datos incorrectos</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[#1d8fce] rounded-full"></div>
                          <span className="font-semibold">Actualización:</span>
                          <span className="text-gray-600">Mantener datos al día</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[#1d8fce] rounded-full"></div>
                          <span className="font-semibold">Supresión:</span>
                          <span className="text-gray-600">Solicitar eliminación</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                      <p className="text-gray-700 leading-relaxed mb-3">
                        <strong>Para ejercer tus derechos</strong>, podés contactarnos en:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>Email:</strong> <a href="mailto:info@creditomovil.com.ar" className="text-[#1d8fce] hover:underline">info@creditomovil.com.ar</a></li>
                        <li><strong>Teléfono:</strong> 02946-454638</li>
                        <li><strong>Dirección:</strong> 25 de Mayo 528, Esquel, Chubut</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl">
                      <p className="text-gray-700 leading-relaxed">
                        También podés contactar a la <strong>Agencia de Acceso a la Información Pública</strong>: <a href="https://www.argentina.gob.ar/aaip" target="_blank" rel="noopener noreferrer" className="text-[#1d8fce] hover:underline font-semibold">argentina.gob.ar/aaip</a>
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Data Fiscal */}
              <AccordionItem value="legal-4" className="bg-white rounded-2xl px-6 border-2 border-gray-200">
                <AccordionTrigger className="text-left text-xl font-bold hover:text-[#1d8fce]">
                  Data Fiscal
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 space-y-4 pt-4">
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-[#1d8fce]/10 to-white p-6 rounded-xl border-2 border-[#1d8fce]/20">
                      <div className="flex items-center justify-center mb-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_easycredit-2/artifacts/tkapgpw9_logo%20data%20fiscal.jpg"
                          alt="Data Fiscal"
                          className="h-24"
                        />
                      </div>
                      <h3 className="text-2xl font-bold text-center text-gray-900 mb-4">
                        Régimen de Información de Operaciones Crediticias
                      </h3>
                      <p className="text-center text-gray-700 text-lg">
                        En cumplimiento de las normativas vigentes de AFIP
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                      <h4 className="font-bold text-lg text-gray-900 mb-4">¿Qué es Data Fiscal?</h4>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Es un régimen de información implementado por AFIP mediante el cual las entidades financieras y prestadoras de servicios crediticios deben informar las operaciones crediticias otorgadas a sus clientes.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Crédito Móvil S.A.</strong> cumple con todas las obligaciones de información establecidas por la AFIP, garantizando transparencia en todas sus operaciones.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-[#1d8fce]">
                        <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                          <FileCheck className="w-6 h-6 text-[#1d8fce] mr-2" />
                          Información Reportada
                        </h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-[#1d8fce] rounded-full mt-2"></div>
                            <span>Montos de créditos otorgados</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-[#1d8fce] rounded-full mt-2"></div>
                            <span>Plazos y condiciones</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-[#1d8fce] rounded-full mt-2"></div>
                            <span>Estado de las operaciones</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-[#1d8fce] rounded-full mt-2"></div>
                            <span>Datos del tomador del crédito</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
                        <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center">
                          <Shield className="w-6 h-6 text-green-600 mr-2" />
                          Beneficios para Vos
                        </h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                            <span>Transparencia total en las operaciones</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                            <span>Cumplimiento normativo garantizado</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                            <span>Protección de tus derechos</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                            <span>Historial crediticio formal</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-6 rounded-xl border-2 border-[#FDB913]/30">
                      <h4 className="font-bold text-lg text-gray-900 mb-3">Consultá tu información fiscal</h4>
                      <p className="text-gray-700 mb-4">
                        Podés verificar la información reportada sobre tus operaciones crediticias ingresando a:
                      </p>
                      <a 
                        href="https://auth.afip.gob.ar/contribuyente_/login.xhtml?action=SYSTEM&system=denuncias"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-[#1d8fce] hover:bg-[#1678b3] text-white font-bold px-6 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl"
                      >
                        Acceder a AFIP
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Logos moved to footer */}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          {/* Logo & Legal Text */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_easycredit-2/artifacts/icq083yy_credito_movil_logo_transparent.png" 
              alt="Crédito Móvil" 
              className="h-20 mb-4 mx-auto"
            />
            
            <p className="text-gray-400 text-sm mb-4">Más de 20 años de confianza en la Patagonia.</p>
            <p className="text-gray-500 text-xs leading-relaxed max-w-2xl mb-8 mx-auto">
              Sujeto a evaluación y aprobación. Todas las condiciones son informadas antes de confirmar tu solicitud.
            </p>

            {/* Logos de Asociaciones en Footer */}
            <div className="mt-6 mb-8">
              <div className="flex flex-wrap items-center justify-center gap-8">
                <a 
                  href="https://camarafintech.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity duration-300"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_easycredit-2/artifacts/pyef7619_logo%20fintech.png"
                    alt="Cámara Argentina Fintech"
                    className="h-16 md:h-20"
                  />
                </a>
                
                <a 
                  href="https://www.usuariosfinancieros.gob.ar" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity duration-300"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_easycredit-2/artifacts/7wsq3t5y_brca_naranja-1.png"
                    alt="Banco Central de la República Argentina"
                    className="h-16 md:h-20"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Enlaces y Contacto - Centrados */}
          <div className="flex flex-col md:flex-row justify-center items-center md:items-start gap-12 md:gap-20 max-w-4xl mx-auto mb-8">
            {/* Links */}
            <div className="text-center w-full md:w-auto">
              <h4 className="font-bold text-white mb-4 text-lg">Enlaces</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('inicio')} className="text-gray-400 hover:text-white transition-colors">Inicio</button></li>
                <li><button onClick={() => scrollToSection('como-funciona')} className="text-gray-400 hover:text-white transition-colors">Cómo funciona</button></li>
                <li><button onClick={() => scrollToSection('beneficios')} className="text-gray-400 hover:text-white transition-colors">¿Por qué elegirnos?</button></li>
                <li><button onClick={() => scrollToSection('nosotros')} className="text-gray-400 hover:text-white transition-colors">Nosotros</button></li>
                <li><button onClick={() => scrollToSection('preguntas')} className="text-gray-400 hover:text-white transition-colors">Preguntas frecuentes</button></li>
                <li><button onClick={() => scrollToSection('contacto')} className="text-gray-400 hover:text-white transition-colors">Contacto</button></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="text-center w-full md:w-auto">
              <h4 className="font-bold text-white mb-4 text-lg">Contacto</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-center space-x-2">
                  <MapPin className="w-4 h-4 text-[#1d8fce] flex-shrink-0" />
                  <span className="text-gray-400">Esquel: 25 de Mayo 528</span>
                </li>
                <li className="flex items-center justify-center space-x-2">
                  <MapPin className="w-4 h-4 text-[#1d8fce] flex-shrink-0" />
                  <span className="text-gray-400">Trevelin: San Martín 419</span>
                </li>
                <li className="flex items-center justify-center space-x-2">
                  <Phone className="w-4 h-4 text-[#1d8fce] flex-shrink-0" />
                  <span className="text-gray-400">+54 9 2945 331566</span>
                </li>
                <li className="flex items-center justify-center space-x-2">
                  <Mail className="w-4 h-4 text-[#1d8fce] flex-shrink-0" />
                  <span className="text-gray-400">info@creditomovil.com.ar</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8">
            <p className="text-gray-400 text-sm text-center">
              © 2026 Crédito Móvil. Todos los derechos reservados.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <button className="text-gray-400 hover:text-white transition-colors">Términos y Condiciones</button>
              <button onClick={() => window.open('https://creditomovil.com.ar/politicas-de-privacidad/', '_blank')} className="text-gray-400 hover:text-white transition-colors">Privacidad</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Plane CTA with Parrot Pilot */}
      {showPlane && isScrolled && (
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1.5, type: 'spring', stiffness: 80 }}
          className="fixed bottom-20 left-6 z-50 group"
        >
          <motion.div
            animate={{ 
              y: [0, -12, 0],
              x: [0, 5, 0]
            }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            className="relative cursor-pointer"
            onClick={handleCTAClick}
          >
            {/* Parrot Pilot Image */}
            <div className="relative transform hover:scale-105 transition-all duration-300 filter drop-shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_credito-movil/artifacts/mmg1p3ve_012F0F49-EA03-4A92-A2D0-90A8CED7AB8B.PNG"
                alt="Tu crédito online en segundos"
                className="w-56 h-auto md:w-72 lg:w-80"
              />
              
              {/* Motion lines for speed effect */}
              <motion.div
                animate={{ opacity: [0.2, 0.6, 0.2], x: [-8, -20, -8] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute top-1/2 -left-10 transform -translate-y-1/2"
              >
                <div className="space-y-2">
                  <div className="w-10 h-1 bg-white rounded-full opacity-60"></div>
                  <div className="w-8 h-1 bg-white rounded-full opacity-50"></div>
                  <div className="w-6 h-1 bg-white rounded-full opacity-40"></div>
                </div>
              </motion.div>
            </div>
            
            {/* Close button - Left side, Yellow, Smaller */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPlane(false);
              }}
              className="absolute -top-1 -left-1 bg-[#FDB913] hover:bg-[#FCA311] text-white rounded-full w-5 h-5 flex items-center justify-center shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Cerrar"
            >
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* Floating WhatsApp Button */}
      {isScrolled && (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: 'spring' }}
        className="fixed bottom-20 right-6 z-50 group"
      >
        <button
          onClick={handleWhatsAppClick}
          className="bg-[#25D366] hover:bg-[#20BA5A] text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all hover:scale-110"
          aria-label="Contactar por WhatsApp"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
        <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg whitespace-nowrap">
            ¿Necesitás ayuda?
          </div>
        </div>
      </motion.div>
      )}
    </div>
  );
};

// Counter Card Component
const CounterCard = ({ number, suffix = '', label }) => {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  
  return (
    <div ref={ref} className="bg-gradient-to-br from-[#1d8fce] to-[#1678b3] p-6 rounded-2xl text-white">
      <div className="text-5xl font-bold mb-2">
        {inView && <CountUp end={number} duration={2.5} />}
        {suffix}
      </div>
      <div className="text-lg opacity-90">{label}</div>
    </div>
  );
};

// Client Counter Component - Cuotin Section
const ClientCounter = ({ inView }) => {
  return (
    <div className="inline-block">
      <div className="bg-white rounded-3xl px-8 py-6 md:px-12 md:py-8 shadow-2xl border-2 border-[#1d8fce]/20 will-change-auto">
        <div className="text-6xl md:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-[#1d8fce] to-[#1678b3] bg-clip-text text-transparent mb-2 min-h-[80px] md:min-h-[96px] lg:min-h-[112px] flex items-center justify-center tabular-nums">
          {inView ? (
            <>
              +<CountUp end={5000} duration={2.5} separator="." preserveValue useEasing />
            </>
          ) : (
            <span className="opacity-0">+5.000</span>
          )}
        </div>
        <div className="text-xl md:text-2xl font-semibold text-gray-800">
          clientes satisfechos
        </div>
      </div>
    </div>
  );
};

// Counter with Cuotin Component - Shared trigger, independent animations
const CounterWithCuotin = () => {
  const { ref, inView } = useInView({ 
    threshold: 0.3, 
    triggerOnce: true 
  });
  
  return (
    <div ref={ref} className="inline-block relative">
      {/* Contador - posición estática */}
      <div className="relative z-10">
        <ClientCounter inView={inView} />
      </div>
      
      {/* Cuotin - Desktop - A la derecha del contador, mitad superpuesta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
        className="absolute top-1/2 -right-32 transform -translate-y-1/2 hidden lg:block z-20"
        style={{ pointerEvents: 'none' }}
      >
        <motion.div
          animate={inView ? { 
            y: [0, -6, 0],
          } : {}}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2.5
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_credito-movil/artifacts/dm79wgmd_ED68E36C-4FDB-4190-82E1-901AA7C685AE%20%281%29.PNG"
            alt="Cuotin - Mascota Crédito Móvil"
            className="w-64 h-auto drop-shadow-2xl"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

// Trust Badge Component for Carousel
const TrustBadge = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center space-y-2 min-w-[150px]">
    <div className="bg-gradient-to-br from-[#1d8fce] to-[#1678b3] p-4 rounded-full">
      <Icon className="w-8 h-8 text-white" />
    </div>
    <p className="text-sm font-semibold text-gray-700 text-center">{text}</p>
  </div>
);

export default Home;
